import React, { useMemo, useState } from 'react';
import {
  BookOpen,
  CheckCircle,
  ChevronRight,
  Clock,
  ExternalLink,
  Globe2,
  Link2,
  Search,
} from 'lucide-react';
import type { DictNode } from '../../types/workspaceState';
import type { OnlineReference, OnlineSupport } from '../../types/online_support';
import { isSafeUrl } from '../../utils/urlSafety';
import { formatDateTime } from '../../utils/formatDateTime';
import { formatCount } from '../../utils/plural';

interface OnlineSupportDetailViewProps {
  support: OnlineSupport;
  nodes?: Record<string, DictNode>;
  onNavigateToNode?: (nodeId: string) => void;
}

function formatDate(value?: string | null): string {
  if (!value) return '';
  return formatDateTime(value, { invalidFallback: value });
}

function formatLabel(value: string): string {
  return value.replace(/_/g, ' ');
}

function hostFromUrl(url?: string | null): string {
  if (!url) return '';
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function ReferenceRow({
  reference,
  isExpanded,
  onToggle,
}: {
  reference: OnlineReference;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const title = reference.title || hostFromUrl(reference.url) || reference.url;
  const domain = reference.domain || hostFromUrl(reference.url);
  // Only http(s) URLs become live links; anything else (javascript:,
  // data:, …) renders as inert text so an injected reference can't XSS.
  const safeUrl = isSafeUrl(reference.url) ? reference.url : null;

  return (
    <div className="border border-border-default rounded-lg bg-surface overflow-hidden">
      <div className="w-full px-3 py-2 flex items-start gap-2 hover:bg-bg-warm transition-colors">
        <button
          type="button"
          onClick={onToggle}
          aria-label={isExpanded ? 'Collapse reference' : 'Expand reference'}
          className="mt-0.5 flex-shrink-0 text-fg-tertiary hover:text-fg-secondary transition-colors"
        >
          <ChevronRight
            size={14}
            strokeWidth={1.5}
            className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`}
          />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            {safeUrl ? (
              <a
                href={safeUrl}
                target="_blank"
                rel="noopener noreferrer"
                title={`Open: ${safeUrl}`}
                className="text-sm font-medium text-link hover:underline truncate inline-flex items-center gap-1.5 min-w-0"
              >
                <span className="truncate">{title}</span>
                <ExternalLink size={11} strokeWidth={1.5} className="flex-shrink-0" />
              </a>
            ) : (
              <p className="text-sm font-medium text-fg truncate">{title}</p>
            )}
            {domain && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-bg-warm text-fg-tertiary flex-shrink-0">
                {domain}
              </span>
            )}
          </div>
          {reference.snippet && (
            <button
              type="button"
              onClick={onToggle}
              className="block w-full text-left mt-1 text-xs text-fg-secondary line-clamp-2 hover:text-fg-secondary/80"
            >
              {reference.snippet}
            </button>
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="px-3 pb-3 border-t border-divider bg-surface-sunken">
          {reference.why_relevant && (
            <div className="mt-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-fg-tertiary mb-1">
                Why it mattered
              </div>
              <p className="text-xs text-fg-secondary leading-relaxed">
                {reference.why_relevant}
              </p>
            </div>
          )}
          {reference.snippet && (
            <div className="mt-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-fg-tertiary mb-1">
                Source snippet
              </div>
              <p className="text-xs text-fg-secondary leading-relaxed whitespace-pre-wrap">
                {reference.snippet}
              </p>
            </div>
          )}
          <div className="mt-3 flex items-center justify-between gap-3">
            {reference.published_date ? (
              <span className="text-xs text-fg-tertiary">
                Published {reference.published_date}
              </span>
            ) : (
              <span />
            )}
            {safeUrl ? (
              <a
                href={safeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-link hover:border-b hover:border-link"
              >
                Open source
                <ExternalLink size={12} strokeWidth={1.5} />
              </a>
            ) : (
              <span />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function OnlineSupportDetailView({
  support,
  nodes,
  onNavigateToNode,
}: OnlineSupportDetailViewProps) {
  const [expandedReferences, setExpandedReferences] = useState<Set<number>>(new Set([0]));

  const linkedNode = support.linked_node_id && nodes
    ? nodes[support.linked_node_id]
    : null;

  const references = support.references || [];
  // Resolve the primary source to a http(s)-only URL; a non-http(s)
  // external_url / first-reference url is dropped so it never renders
  // as a clickable anchor.
  const primaryCandidate = support.external_url || references[0]?.url || null;
  const primarySourceUrl = isSafeUrl(primaryCandidate) ? primaryCandidate : null;

  const metadataRows = useMemo(() => ([
    { label: 'Purpose', value: formatLabel(support.purpose) },
    { label: 'Status', value: formatLabel(support.status) },
    { label: 'Query', value: support.query },
    { label: 'Retrieved', value: formatDate(support.retrieved_at) },
    { label: 'Pinned', value: formatDate(support.pinned_at) },
    { label: 'External host', value: support.external_host },
    { label: 'BARK document', value: support.bark_document_id },
  ].filter((row) => row.value)), [support]);

  const toggleReference = (index: number) => {
    setExpandedReferences((current) => {
      const next = new Set(current);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  return (
    <div className="h-full overflow-y-auto bg-surface">
      <div className="p-4 space-y-4">
        <div className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-bg-warm text-fg-secondary text-xs font-semibold">
              <Globe2 size={12} strokeWidth={1.5} />
              Online support
            </span>
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-surface-sunken text-fg-tertiary text-xs">
              <Search size={12} strokeWidth={1.5} />
              {formatLabel(support.purpose)}
            </span>
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-surface-sunken text-fg-tertiary text-xs">
              <CheckCircle size={12} strokeWidth={1.5} />
              {formatLabel(support.status)}
            </span>
          </div>

          <h3 className="text-sm font-semibold text-fg leading-snug">
            {support.rationale}
          </h3>
          <p className="text-xs text-fg-secondary leading-relaxed">
            {support.summary}
          </p>
        </div>

        {linkedNode && support.linked_node_id && (
          <button
            type="button"
            onClick={() => onNavigateToNode?.(support.linked_node_id!)}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-border-default bg-surface-sunken text-left hover:border-border-strong transition-colors"
          >
            <Link2 size={14} strokeWidth={1.5} className="text-fg-tertiary flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-xs uppercase tracking-wide font-semibold text-fg-tertiary">
                Linked node
              </p>
              <p className="text-xs text-fg-secondary truncate">{linkedNode.label}</p>
            </div>
          </button>
        )}

        <dl className="rounded-lg border border-border-default bg-surface-sunken p-3 space-y-2 text-xs">
            {metadataRows.map((row) => (
              <div key={row.label} className="grid grid-cols-3 gap-3">
                <dt className="text-fg-tertiary">{row.label}</dt>
                <dd className="text-fg-secondary min-w-0 break-words">{row.value}</dd>
              </div>
            ))}
        </dl>

        {(support.report || support.source_pointer || support.affirmative_source) && (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-fg-tertiary mb-2">
              Search conclusion
            </div>
            <div className="rounded-lg border border-border-default bg-surface p-3 space-y-3">
              {support.report && (
                <p className="text-sm text-fg-secondary leading-relaxed whitespace-pre-wrap">
                  {support.report}
                </p>
              )}
              {support.source_pointer && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-fg-tertiary mb-1">
                    Source pointer
                  </div>
                  <p className="text-xs text-fg-secondary leading-relaxed whitespace-pre-wrap">
                    {support.source_pointer}
                  </p>
                </div>
              )}
              {support.affirmative_source && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-fg-tertiary mb-1">
                    Affirmative source
                  </div>
                  <p className="text-xs text-fg-secondary leading-relaxed whitespace-pre-wrap">
                    {support.affirmative_source}
                  </p>
                </div>
              )}
              {primarySourceUrl && (
                <a
                  href={primarySourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-link hover:border-b hover:border-link"
                >
                  Open primary source
                  <ExternalLink size={12} strokeWidth={1.5} />
                </a>
              )}
            </div>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-fg-tertiary">
              <BookOpen size={12} strokeWidth={1.5} />
              References
            </div>
            <span className="text-xs text-fg-tertiary">
              {formatCount(references.length, { one: 'source', other: 'sources' })}
            </span>
          </div>
          {references.length > 0 ? (
            <div className="space-y-2">
              {references.map((reference, index) => (
                <ReferenceRow
                  // Stable, index-independent key. url is the natural id, but
                  // it can be blanked by the URL-safety filter, so fall back to
                  // a title+url composite to stay unique-ish without the array
                  // index (which reshuffles keys on reorder/insert).
                  key={reference.url || `${reference.title}-${reference.url}`}
                  reference={reference}
                  isExpanded={expandedReferences.has(index)}
                  onToggle={() => toggleReference(index)}
                />
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-border-default bg-surface-sunken p-4 text-center">
              <p className="text-sm text-fg-tertiary">No web references were saved for this support item.</p>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1.5 text-xs text-fg-tertiary">
          <Clock size={12} strokeWidth={1.5} />
          Retrieved {formatDate(support.retrieved_at) || 'unknown time'}
        </div>
      </div>
    </div>
  );
}
